import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

/**
 * Hierarchical Stock View Redux Slice
 *
 * Provides 4-level drill-down capability for item stock:
 * Level 1: Item Summary (total across all locations)
 * Level 2: Stock by Location
 * Level 3: Stock by Warehouse (within a location)
 * Level 4: Stock by Bin (within a warehouse, for BinEnabled warehouses only)
 */

// Level 1: Get item stock summary (total across all locations)
export const getItemStockSummary = createAsyncThunk(
  "stockHierarchy/getItemSummary",
  async (itemId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.STOCK.HIERARCHY.ITEM_SUMMARY(itemId));
      return data.data || data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Level 2: Get stock breakdown by location
export const getStockByLocation = createAsyncThunk(
  "stockHierarchy/getByLocation",
  async (itemId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.STOCK.HIERARCHY.BY_LOCATION(itemId));
      return data.data || data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Level 3: Get stock breakdown by warehouse (within a location)
export const getStockByWarehouse = createAsyncThunk(
  "stockHierarchy/getByWarehouse",
  async ({ itemId, locationId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.STOCK.HIERARCHY.BY_WAREHOUSE(itemId, locationId)
      );
      return { locationId, warehouses: data.data || data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Level 4: Get stock breakdown by bin (within a warehouse)
export const getStockByBin = createAsyncThunk(
  "stockHierarchy/getByBin",
  async ({ itemId, warehouseId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.STOCK.HIERARCHY.BY_BIN(itemId, warehouseId)
      );
      return { warehouseId, bins: data.data || data };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const stockHierarchySlice = createSlice({
  name: "stockHierarchy",
  initialState: {
    // Level 1: Item summary
    itemSummary: null,
    itemSummaryLoading: false,
    itemSummaryError: null,

    // Level 2: Locations list
    locations: [],
    locationsLoading: false,
    locationsError: null,

    // Level 3: Warehouses by location (keyed by locationId)
    warehousesByLocation: {},
    warehousesLoading: {},
    warehousesError: {},

    // Level 4: Bins by warehouse (keyed by warehouseId)
    binsByWarehouse: {},
    binsLoading: {},
    binsError: {},

    // Current drill-down state
    selectedItemId: null,
    selectedLocationId: null,
    selectedWarehouseId: null,
  },
  reducers: {
    // Reset all hierarchy data (when switching items)
    resetHierarchy(state) {
      state.itemSummary = null;
      state.itemSummaryLoading = false;
      state.itemSummaryError = null;
      state.locations = [];
      state.locationsLoading = false;
      state.locationsError = null;
      state.warehousesByLocation = {};
      state.warehousesLoading = {};
      state.warehousesError = {};
      state.binsByWarehouse = {};
      state.binsLoading = {};
      state.binsError = {};
      state.selectedItemId = null;
      state.selectedLocationId = null;
      state.selectedWarehouseId = null;
    },

    // Set selected item
    setSelectedItem(state, action) {
      state.selectedItemId = action.payload;
    },

    // Set selected location (for drilling down)
    setSelectedLocation(state, action) {
      state.selectedLocationId = action.payload;
      state.selectedWarehouseId = null; // Reset warehouse selection
    },

    // Set selected warehouse (for drilling down to bins)
    setSelectedWarehouse(state, action) {
      state.selectedWarehouseId = action.payload;
    },

    // Clear warehouse data for a location (when collapsing)
    clearWarehouseData(state, action) {
      const locationId = action.payload;
      delete state.warehousesByLocation[locationId];
      delete state.warehousesLoading[locationId];
      delete state.warehousesError[locationId];
    },

    // Clear bin data for a warehouse (when collapsing)
    clearBinData(state, action) {
      const warehouseId = action.payload;
      delete state.binsByWarehouse[warehouseId];
      delete state.binsLoading[warehouseId];
      delete state.binsError[warehouseId];
    },
  },
  extraReducers: (builder) => {
    builder
      // Item Summary (Level 1)
      .addCase(getItemStockSummary.pending, (state) => {
        state.itemSummaryLoading = true;
        state.itemSummaryError = null;
      })
      .addCase(getItemStockSummary.fulfilled, (state, action) => {
        state.itemSummary = action.payload;
        state.itemSummaryLoading = false;
      })
      .addCase(getItemStockSummary.rejected, (state, action) => {
        state.itemSummaryLoading = false;
        state.itemSummaryError = action.payload;
      })

      // Locations (Level 2)
      .addCase(getStockByLocation.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(getStockByLocation.fulfilled, (state, action) => {
        state.locations = action.payload || [];
        state.locationsLoading = false;
      })
      .addCase(getStockByLocation.rejected, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = action.payload;
      })

      // Warehouses (Level 3)
      .addCase(getStockByWarehouse.pending, (state, action) => {
        const { locationId } = action.meta.arg;
        state.warehousesLoading[locationId] = true;
        state.warehousesError[locationId] = null;
      })
      .addCase(getStockByWarehouse.fulfilled, (state, action) => {
        const { locationId, warehouses } = action.payload;
        state.warehousesByLocation[locationId] = warehouses || [];
        state.warehousesLoading[locationId] = false;
      })
      .addCase(getStockByWarehouse.rejected, (state, action) => {
        const { locationId } = action.meta.arg;
        state.warehousesLoading[locationId] = false;
        state.warehousesError[locationId] = action.payload;
      })

      // Bins (Level 4)
      .addCase(getStockByBin.pending, (state, action) => {
        const { warehouseId } = action.meta.arg;
        state.binsLoading[warehouseId] = true;
        state.binsError[warehouseId] = null;
      })
      .addCase(getStockByBin.fulfilled, (state, action) => {
        const { warehouseId, bins } = action.payload;
        state.binsByWarehouse[warehouseId] = bins || [];
        state.binsLoading[warehouseId] = false;
      })
      .addCase(getStockByBin.rejected, (state, action) => {
        const { warehouseId } = action.meta.arg;
        state.binsLoading[warehouseId] = false;
        state.binsError[warehouseId] = action.payload;
      });
  },
});

export const {
  resetHierarchy,
  setSelectedItem,
  setSelectedLocation,
  setSelectedWarehouse,
  clearWarehouseData,
  clearBinData,
} = stockHierarchySlice.actions;

export default stockHierarchySlice.reducer;
